// @ts-nocheck
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getCached, setCached, swrLoad, invalidate } from './dataCache';

/* ──────────────────────────────────────────────────────────────────────────
   Inventory — engineering sample tracking
   Three sections: In Stock · Testing · In Development (+ Archive for terminal
   states). Detail slide-over supports edit, attachments, section moves, and a
   per-part activity log. Aging is surfaced for Testing (overdue return) and
   In Development (sitting too long).
   ────────────────────────────────────────────────────────────────────────── */

const _t = () => (window as any).__ENGPORTAL_THEME || null;
const _dark = { bg: "#0B1120", surface: "#111827", surfaceHover: "#1a2332", border: "#1E293B", borderLight: "#334155", text: "#E2E8F0", textMuted: "#94A3B8", textDim: "#64748B", accent: "#3B82F6", accentGlow: "rgba(59, 130, 246, 0.15)", green: "#10B981", yellow: "#F59E0B", red: "#EF4444", purple: "#8B5CF6", orange: "#F97316", cardShadow: "none", mode: "dark" };
const theme = new Proxy(_dark, { get: (fallback, prop) => { const t = _t(); return t ? t[prop] : fallback[prop]; } });

// Days a sample may sit in a section before it's flagged as needing attention.
const STALE_DAYS = { testing: 90, in_development: 60 };

const SECTIONS = {
  in_inventory:   { label: 'In Stock',       color: () => theme.green },
  testing:        { label: 'Testing',        color: () => theme.yellow },
  in_development: { label: 'In Development',  color: () => theme.accent },
};
const ARCHIVE_STATUSES = ['testing_complete', 'allocated', 'shipped', 'consumed', 'scrapped'];

const CLASS_CFG = {
  production:  { label: 'Production',  color: () => theme.green },
  development: { label: 'Development', color: () => theme.accent },
  trial:       { label: 'Trial',      color: () => theme.purple },
};

const OUTCOME_CFG = {
  pending:      { label: 'Pending',      color: () => theme.textMuted },
  pass:         { label: 'Pass',         color: () => theme.green },
  fail:         { label: 'Fail',         color: () => theme.red },
  inconclusive: { label: 'Inconclusive', color: () => theme.yellow },
};

const STATUS_LABEL = {
  in_inventory: 'In Stock', testing: 'Testing', in_development: 'In Development',
  testing_complete: 'Testing Complete', allocated: 'Allocated', shipped: 'Shipped',
  consumed: 'Consumed', scrapped: 'Scrapped',
};

const ATTACHMENT_TYPES = [
  ['material_spec', 'Material Specification'], ['test_report', 'Test Report'],
  ['test_data', 'Raw Test Data'], ['route_sheet', 'Route Sheet'],
  ['certificate', 'Certificate of Analysis'], ['image', 'Photo / Image'],
  ['sds', 'Safety Data Sheet'], ['drawing', 'Engineering Drawing'],
  ['bom', 'Bill of Materials'], ['other', 'Other'],
];
const ATTACHMENT_LABEL = Object.fromEntries(ATTACHMENT_TYPES);

const UNIT_OPTIONS = ['pcs', 'sheets', 'rolls', 'kg', 'lbs', 'liters', 'meters', 'ft', 'sets'];

/* ── small helpers ─────────────────────────────────────────────────────────── */
const csrf = () => { const m = document.cookie.match(/csrftoken=([^;]+)/); return m ? m[1] : ''; };
const todayISO = () => new Date().toISOString().slice(0, 10);
const addDaysISO = (iso, n) => { const d = iso ? new Date(iso) : new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const fmtDate = (iso) => { if (!iso) return '—'; const d = new Date(iso); return isNaN(d) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); };
const fmtDateTime = (iso) => { if (!iso) return ''; const d = new Date(iso); return isNaN(d) ? '' : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); };
const qty = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : (Number.isInteger(n) ? n : n.toFixed(2).replace(/\.?0+$/, '')); };

async function fetchAll(url) {
  let out = []; let next = url; let guard = 0;
  while (next && guard < 25) {
    // DRF returns absolute `next` URLs (http://…). On an HTTPS page the browser
    // blocks those as mixed content, breaking pagination past page 1. Convert
    // any absolute URL to a same-origin relative path before fetching.
    if (next && /^https?:\/\//i.test(next)) {
      try { const u = new URL(next); next = u.pathname + u.search; } catch (e) { /* keep as-is */ }
    }
    const res = await fetch(next, { credentials: 'same-origin' });
    if (!res.ok) break;
    const data = await res.json();
    if (Array.isArray(data)) { out = out.concat(data); break; }
    out = out.concat(data.results || []);
    next = data.next; guard++;
  }
  return out;
}

async function jsonReq(method, url, body) {
  const res = await fetch(url, {
    method, credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf() },
    body: body != null ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try { data = await res.json(); } catch (e) { /* 204 / empty */ }
  if (!res.ok) {
    const msg = (data && (data.detail || (typeof data === 'object' ? JSON.stringify(data) : data))) || res.statusText;
    throw new Error(msg);
  }
  return data;
}

const isTestingOverdue = (p) =>
  p.status === 'testing' && (p.is_test_overdue ||
    (p.expected_return_date && new Date(p.expected_return_date) < new Date(todayISO())));
const isDevStale = (p) =>
  p.status === 'in_development' && (p.days_in_section || 0) > STALE_DAYS.in_development;
const needsAttention = (p) => isTestingOverdue(p) || isDevStale(p);

/* ── reusable bits ─────────────────────────────────────────────────────────── */
function Badge({ label, color }) {
  return <span style={{ background: color + '22', color, fontSize: 10, padding: '2px 7px', borderRadius: 4, fontWeight: 600, whiteSpace: 'nowrap' }}>{label}</span>;
}

function Modal({ open, onClose, title, width = 560, children }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '60px 16px', overflow: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ width, maxWidth: '100%', background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 14, padding: 22 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ color: theme.text, fontSize: 16, fontWeight: 700 }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: theme.textDim, fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function InventoryPage({ openModal, pendingNav, clearPendingNav }) {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('in_inventory');
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('all');

  const [programs, setPrograms] = useState([]);
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);

  const [selected, setSelected] = useState(null);     // part in detail panel
  const [addOpen, setAddOpen] = useState(false);
  const [trialOpen, setTrialOpen] = useState(false);
  const [shipOpen, setShipOpen] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [customers, setCustomers] = useState([]);

  /* styles built at render so theme is current */
  const input = { width: '100%', padding: '9px 12px', background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text, fontSize: 13, outline: 'none', boxSizing: 'border-box' };
  const label = { color: theme.textDim, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 5, display: 'block' };
  const btnPrimary = { padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: theme.green, color: '#fff', border: 'none', cursor: 'pointer' };
  const btnGhost = { padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: 'transparent', color: theme.textMuted, border: `1px solid ${theme.border}`, cursor: 'pointer' };

  const loadParts = useCallback(async (opts) => {
    await swrLoad('inv:parts', () => fetchAll('/api/inventory/parts/'), setParts, opts);
    setLoading(false);
  }, []);

  const fetchShipments = useCallback(async (q) => {
    let url = '/api/inventory/shipments/' + (q ? `?search=${encodeURIComponent(q)}` : '');
    const all = [];
    let guard = 0;
    while (url && guard < 100) {
      const d = await fetch(url, { credentials: 'same-origin' }).then(r => r.ok ? r.json() : []);
      if (Array.isArray(d)) { all.push(...d); url = null; }
      else {
        all.push(...(d.results || []));
        if (d.next) { try { const u = new URL(d.next); url = u.pathname + u.search; } catch { url = d.next; } }
        else { url = null; }
      }
      guard++;
    }
    return all;
  }, []);

  const loadShipments = useCallback(async (q, opts) => {
    try {
      if (q) {
        // A search query bypasses the cache (server-side filtered result).
        setShipments(await fetchShipments(q));
      } else {
        await swrLoad('inv:shipments', () => fetchShipments(), setShipments, opts);
      }
    } catch (e) { setShipments([]); }
  }, [fetchShipments]);

  useEffect(() => {
    // Instant paint from cache (if present) before any network call.
    const cp = getCached('inv:parts'); if (cp) { setParts(cp); setLoading(false); }
    const cs = getCached('inv:shipments'); if (cs) setShipments(cs);
    ['programs', 'users', 'inv:locations', 'inv:categories', 'customers'].forEach(k => {
      const v = getCached(k);
      if (v) { const set = { programs: setPrograms, users: setUsers, 'inv:locations': setLocations, 'inv:categories': setCategories, customers: setCustomers }[k]; set && set(v); }
    });
    // Then load through the cache: fresh → no network; stale → fetch + update.
    loadParts();
    loadShipments();
    swrLoad('programs',       () => fetchAll('/api/programs/programs/'),      setPrograms);
    swrLoad('users',          () => fetchAll('/api/auth/users/'),             setUsers);
    swrLoad('inv:locations',  () => fetchAll('/api/inventory/locations/'),    setLocations);
    swrLoad('inv:categories', () => fetchAll('/api/inventory/categories/'),   setCategories);
    swrLoad('customers',      () => fetchAll('/api/customers/customers/'),    setCustomers);
  }, [loadParts, loadShipments]);

  // When arriving from the dashboard "Add Sample" quick action, open the real
  // New Trial Sample modal (and switch to the trial tab for context), then clear
  // the signal so it doesn't re-fire on the next visit.
  useEffect(() => {
    if (pendingNav === 'new-trial') {
      setTab('in_development');
      setTrialOpen(true);
      clearPendingNav && clearPendingNav();
    }
  }, [pendingNav, clearPendingNav]);

  // On the Sent Samples tab, search runs server-side against shipments.
  useEffect(() => {
    if (tab !== 'testing') return;
    const t = setTimeout(() => loadShipments(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search, tab, loadShipments]);

  const counts = useMemo(() => {
    const c = { in_inventory: 0, testing: 0, in_development: 0, archive: 0, attention: 0 };
    for (const p of parts) {
      if (p.archived) { c.archive++; continue; }  // archived parts live only in Archive
      // In Stock = status in_inventory OR any on-hand quantity across lots.
      // (A production part registered with 0 qty still belongs in In Stock, so
      // it stays visible instead of falling through every tab. A trial part in
      // 'in_development' with stock still counts under both, as before.)
      if (p.status === 'in_inventory' || Number(p.total_quantity || 0) > 0) c.in_inventory++;
      if (p.status === 'testing') c.testing++;
      else if (p.status === 'in_development') c.in_development++;
      else if (!(p.status in SECTIONS)) c.archive++;
      if (needsAttention(p)) c.attention++;
    }
    return c;
  }, [parts]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = parts.filter(p => {
      // Archived parts appear ONLY in the Archive tab; every other tab excludes them.
      if (tab === 'archive') {
        if (!(p.archived || ARCHIVE_STATUSES.includes(p.status))) return false;
      } else {
        if (p.archived) return false;
        // In Stock = status in_inventory OR on-hand quantity > 0, so registered
        // production parts (even at 0 qty) stay visible instead of vanishing.
        let inTab;
        if (tab === 'in_inventory') inTab = p.status === 'in_inventory' || Number(p.total_quantity || 0) > 0;
        else inTab = p.status === tab;
        if (!inTab) return false;
      }
      if (classFilter !== 'all' && p.classification !== classFilter) return false;
      if (q && !(`${p.part_number} ${p.name} ${p.test_company || ''}`.toLowerCase().includes(q))) return false;
      return true;
    });
    // Newest first: by created_at when available, else by part number descending.
    filtered.sort((a, b) => {
      const ta = a.created_at ? Date.parse(a.created_at) : 0;
      const tb = b.created_at ? Date.parse(b.created_at) : 0;
      if (tb !== ta) return tb - ta;
      return String(b.part_number || '').localeCompare(String(a.part_number || ''));
    });
    return filtered;
  }, [parts, tab, classFilter, search]);

  // keep the selected part fresh after refreshes
  const refreshAndSelect = async (id) => {
    const all = await fetchAll('/api/inventory/parts/');
    setCached('inv:parts', all);
    setParts(all);
    if (id != null) { const found = all.find(p => p.id === id); if (found) setSelected(found); }
  };

  const TABS = [
    ['in_inventory', `In Stock (${counts.in_inventory})`],
    ['testing', `Sent Samples (${shipments.length})`],
    ['in_development', `Sample Parts List (${counts.in_development})`],
    ['archive', `Archive (${counts.archive})`],
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
      {/* header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ color: theme.text, fontSize: 20, fontWeight: 700, margin: 0 }}>Samples</h2>
          <div style={{ color: theme.textDim, fontSize: 12, marginTop: 3 }}>Customer sample tracking — stock, testing, and development</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {(tab === 'in_development' || tab === 'in_inventory') && (
            <button style={btnPrimary} onClick={() => setTrialOpen(true)}>+ New Trial Sample</button>
          )}
          {tab === 'testing'
            ? <button style={btnPrimary} onClick={() => setShipOpen(true)}>+ New Shipment</button>
            : <button style={btnPrimary} onClick={() => setAddOpen(true)}>+ Add Part</button>}
        </div>
      </div>

      {/* stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        {[
          { l: 'In Stock', v: counts.in_inventory, c: theme.green },
          { l: 'Sent Samples', v: shipments.length, c: theme.yellow },
          { l: 'Sample Parts', v: counts.in_development, c: theme.accent },
          { l: 'Needs Attention', v: counts.attention, c: counts.attention ? theme.red : theme.textMuted },
        ].map(s => (
          <div key={s.l} style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 10, padding: '12px 16px' }}>
            <div style={{ color: theme.textDim, fontSize: 10, textTransform: 'uppercase', marginBottom: 2, letterSpacing: 0.3 }}>{s.l}</div>
            <div style={{ color: s.c, fontSize: 24, fontWeight: 700 }}>{s.v}</div>
          </div>
        ))}
      </div>

      {/* tabs */}
      <div style={{ display: 'flex', gap: 6, borderBottom: `1px solid ${theme.border}`, paddingBottom: 0 }}>
        {TABS.map(([id, lbl]) => (
          <button key={id} onClick={() => setTab(id)} style={{
            padding: '9px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: 'transparent',
            border: 'none', borderBottom: tab === id ? `2px solid ${theme.accent}` : '2px solid transparent',
            color: tab === id ? theme.text : theme.textMuted, marginBottom: -1,
          }}>{lbl}</button>
        ))}
      </div>

      {/* toolbar */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220, position: 'relative' }}>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={tab === 'testing' ? 'Search customer, part #, lot, sales contact…' : 'Search part #, name, company…'}
            style={{ ...input, padding: '10px 14px 10px 34px' }} />
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={theme.textDim} strokeWidth="2" style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }}><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
        </div>
        {tab !== 'testing' && (
          <div style={{ display: 'flex', gap: 6 }}>
            {['all', 'production', 'trial'].map(c => (
              <button key={c} onClick={() => setClassFilter(c)} style={{
                padding: '6px 11px', borderRadius: 6, fontSize: 11, cursor: 'pointer', textTransform: 'capitalize',
                border: classFilter === c ? `1px solid ${theme.accent}` : `1px solid ${theme.border}`,
                background: classFilter === c ? theme.accentGlow : 'transparent',
                color: classFilter === c ? theme.accent : theme.textMuted,
              }}>{c}</button>
            ))}
          </div>
        )}
      </div>

      {/* table */}
      {loading ? (
        <div style={{ color: theme.textDim, padding: 40, textAlign: 'center' }}>Loading…</div>
      ) : (
        <div style={{ flex: 1, overflow: 'auto', background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 12 }}>
          {tab === 'testing'
            ? <ShipmentLog rows={shipments} onOpen={setSelectedShipment} />
            : <PartTable tab={tab} rows={visible} onOpen={setSelected} />}
        </div>
      )}

      {addOpen && (
        <AddPartModal
          open={addOpen} onClose={() => setAddOpen(false)}
          programs={programs} users={users} locations={locations} categories={categories}
          styles={{ input, label, btnPrimary, btnGhost }}
          onCreated={async () => { setAddOpen(false); await loadParts({ force: true }); }}
        />
      )}

      {trialOpen && (
        <AddTrialModal
          open={trialOpen} onClose={() => setTrialOpen(false)}
          styles={{ input, label, btnPrimary, btnGhost }}
          onCreated={async () => { setTrialOpen(false); await loadParts({ force: true }); }}
        />
      )}

      {shipOpen && (
        <AddShipmentModal
          open={shipOpen} onClose={() => setShipOpen(false)}
          customers={customers} users={users} parts={parts}
          styles={{ input, label, btnPrimary, btnGhost }}
          onCreated={async () => { setShipOpen(false); await loadShipments(undefined, { force: true }); await loadParts({ force: true }); }}
        />
      )}

      {selectedShipment && (
        <ShipmentDetail shipment={selectedShipment} onClose={() => setSelectedShipment(null)} />
      )}

      {selected && (
        <DetailPanel
          part={selected} onClose={() => setSelected(null)}
          programs={programs} users={users} locations={locations} categories={categories}
          styles={{ input, label, btnPrimary, btnGhost }}
          onChanged={(id) => refreshAndSelect(id)}
          onMovedAway={async () => { setSelected(null); await loadParts({ force: true }); }}
        />
      )}
    </div>
  );
}

/* ── table per section ─────────────────────────────────────────────────────── */

/* ── Sent Samples log (read-only in C1; create modal comes in C2) ───────────── */
const SHIP_STATUS_CFG = {
  sent:      { label: 'Sent',     color: () => theme.accent },
  responded: { label: 'Response', color: () => theme.green },
  closed:    { label: 'Closed',   color: () => theme.textMuted },
};

function ShipmentLog({ rows, onOpen }) {
  const grid = '96px 1fr 150px 1fr 150px 96px 90px';
  const cols = ['Date Sent', 'Customer', 'Contact', 'Parts / Lots', 'Sales Contact', 'Follow-Up', 'Status'];
  const partsSummary = (s) => {
    const lines = s.lines || [];
    if (lines.length === 0) return '—';
    return lines.map(l => {
      const pn = l.part_number || l.part_number_text || '—';
      const lot = l.lot_display || l.lot_number_text || '—';
      const flag = l.is_stock_linked ? '' : ' *';
      return `${pn} / ${lot} ×${l.quantity}${flag}`;
    }).join('  ·  ');
  };
  const hasFreeText = (rows || []).some(s => (s.lines || []).some(l => !l.is_stock_linked));

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: grid, gap: 8, padding: '11px 16px', borderBottom: `1px solid ${theme.border}`, position: 'sticky', top: 0, background: theme.surface }}>
        {cols.map(h => <div key={h} style={{ color: theme.textDim, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>{h}</div>)}
      </div>
      {(!rows || rows.length === 0) && <div style={{ padding: 36, textAlign: 'center', color: theme.textDim, fontSize: 13 }}>No shipments logged</div>}
      {(rows || []).map(s => {
        const sc = SHIP_STATUS_CFG[s.status] || SHIP_STATUS_CFG.sent;
        return (
          <div key={s.id} onClick={() => onOpen && onOpen(s)}
            style={{ display: 'grid', gridTemplateColumns: grid, gap: 8, padding: '11px 16px', borderBottom: `1px solid ${theme.border}`, fontSize: 12.5, alignItems: 'center', cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.background = theme.surfaceHover}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <span style={{ color: theme.text }}>{fmtDate(s.date_sent)}</span>
            <span style={{ color: theme.text, fontWeight: 600 }}>{s.customer_name || s.customer_code || '—'}</span>
            <span style={{ color: theme.textMuted }}>{s.contact_display || '—'}</span>
            <span style={{ color: theme.textMuted, fontSize: 11.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{partsSummary(s)}</span>
            <span style={{ color: theme.textMuted }}>{s.sales_contact_name || '—'}</span>
            <span style={{ color: theme.textDim }}>{fmtDate(s.follow_up_date)}</span>
            <Badge label={sc.label} color={sc.color()} />
          </div>
        );
      })}
      {hasFreeText && (
        <div style={{ padding: '8px 16px', color: theme.textDim, fontSize: 11 }}>
          * free-text lot (no stock link — not decremented from inventory)
        </div>
      )}
    </>
  );
}


function ShipmentDetail({ shipment, onClose }) {
  const s = shipment;
  if (!s) return null;
  const sc = SHIP_STATUS_CFG[s.status] || SHIP_STATUS_CFG.sent;
  const field = (label, value) => (
    <div style={{ background: theme.bg, borderRadius: 8, padding: '10px 14px', border: `1px solid ${theme.border}` }}>
      <div style={{ color: theme.textDim, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 }}>{label}</div>
      <div style={{ color: theme.text, fontSize: 13 }}>{value || '—'}</div>
    </div>
  );
  return (
    <Modal open={!!s} onClose={onClose} title="Shipment Detail" width={720}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <span style={{ color: theme.text, fontSize: 18, fontWeight: 700 }}>{s.customer_name || s.customer_code || 'Shipment'}</span>
        <Badge label={sc.label} color={sc.color()} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
        {field('Date Sent', fmtDate(s.date_sent))}
        {field('Contact', s.contact_display)}
        {field('Sales Contact', s.sales_contact_name)}
        {field('Business Type', s.business_type)}
        {field('Order / PO Ref', s.order_reference)}
        {field('Follow-Up Date', fmtDate(s.follow_up_date))}
      </div>

      <div style={{ color: theme.accent, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>Parts Sent</div>
      <div style={{ border: `1px solid ${theme.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px 90px', gap: 8, padding: '8px 12px', background: theme.bg, borderBottom: `1px solid ${theme.border}` }}>
          {['Part #', 'Lot #', 'Qty', 'Stock'].map(h => <div key={h} style={{ color: theme.textDim, fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>{h}</div>)}
        </div>
        {(s.lines || []).length === 0 && <div style={{ padding: 14, color: theme.textDim, fontSize: 12 }}>No line items</div>}
        {(s.lines || []).map(l => (
          <div key={l.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px 90px', gap: 8, padding: '8px 12px', borderBottom: `1px solid ${theme.border}`, fontSize: 12.5, alignItems: 'center' }}>
            <span style={{ color: theme.text }}>{l.part_number || l.part_number_text || '—'}</span>
            <span style={{ color: theme.textMuted }}>{l.lot_display || l.lot_number_text || '—'}</span>
            <span style={{ color: theme.textMuted }}>{l.quantity}</span>
            <span style={{ color: l.is_stock_linked ? theme.green : theme.textDim, fontSize: 11 }}>{l.is_stock_linked ? 'linked' : 'free-text'}</span>
          </div>
        ))}
      </div>

      <div style={{ color: theme.accent, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>Notes / Results</div>
      <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 8, padding: 14, color: theme.textMuted, fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap', minHeight: 40 }}>
        {s.notes_results || '—'}
      </div>
    </Modal>
  );
}

function PartTable({ tab, rows, onOpen }) {
  const cols = {
    in_inventory:   ['Part #', 'Name', 'Class', 'On Hand', 'Unit', 'Lots'],
    testing:        ['Part #', 'Name', 'Class', 'Qty', 'Company', 'Sent', 'Expected', 'Outcome'],
    in_development: ['Part #', 'Name', 'Class', 'Qty', 'Engineer', 'Program', 'Age'],
    archive:        ['Part #', 'Name', 'Class', 'Status', 'Qty', 'Updated'],
  }[tab];
  const grid = {
    in_inventory:   '110px 1fr 96px 70px 60px 1fr',
    testing:        '110px 1fr 96px 56px 150px 96px 96px 96px',
    in_development: '110px 1fr 96px 56px 140px 130px 90px',
    archive:        '110px 1fr 96px 120px 60px 110px',
  }[tab];

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: grid, gap: 8, padding: '11px 16px', borderBottom: `1px solid ${theme.border}`, position: 'sticky', top: 0, background: theme.surface }}>
        {cols.map(h => <div key={h} style={{ color: theme.textDim, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>{h}</div>)}
      </div>
      {rows.length === 0 && <div style={{ padding: 36, textAlign: 'center', color: theme.textDim, fontSize: 13 }}>No parts in this section</div>}
      {rows.map(p => {
        const cls = CLASS_CFG[p.classification] || { label: p.classification, color: () => theme.textDim };
        const overdue = isTestingOverdue(p);
        const stale = isDevStale(p);
        const cells = {
          in_inventory: [
            <span style={{ color: theme.accent, fontWeight: 600 }}>{p.part_number}</span>,
            p.name,
            <Badge label={cls.label} color={cls.color()} />,
            <span style={{ color: theme.text, fontWeight: 600 }}>{qty(p.total_quantity)}</span>,
            <span style={{ color: theme.textDim }}>{p.unit_of_measure}</span>,
            <span style={{ color: theme.textMuted, fontSize: 11 }}>
              {(p.lots || []).filter(l => Number(l.quantity) > 0).map(l => `${l.lot_number} ×${qty(l.quantity)}`).join(', ') || '—'}
            </span>,
          ],
          testing: [
            <span style={{ color: theme.accent, fontWeight: 600 }}>{p.part_number}</span>,
            p.name,
            <Badge label={cls.label} color={cls.color()} />,
            qty(p.quantity),
            <span style={{ color: theme.text }}>{p.test_company || '—'}</span>,
            <span style={{ color: theme.textDim }}>{fmtDate(p.sent_date)}</span>,
            <span style={{ color: overdue ? theme.red : theme.textDim, fontWeight: overdue ? 700 : 400 }}>{fmtDate(p.expected_return_date)}{overdue ? ' ⚠' : ''}</span>,
            <Badge label={(OUTCOME_CFG[p.test_outcome] || OUTCOME_CFG.pending).label} color={(OUTCOME_CFG[p.test_outcome] || OUTCOME_CFG.pending).color()} />,
          ],
          in_development: [
            <span style={{ color: theme.accent, fontWeight: 600 }}>{p.part_number}</span>,
            p.name,
            <Badge label={cls.label} color={cls.color()} />,
            qty(p.quantity),
            <span style={{ color: theme.textMuted }}>{p.assigned_engineer_name || '—'}</span>,
            <span style={{ color: theme.textDim }}>{p.program_code || '—'}</span>,
            <span style={{ color: stale ? theme.red : theme.textDim, fontWeight: stale ? 700 : 400 }}>{p.days_in_section || 0}d{stale ? ' ⚠' : ''}</span>,
          ],
          archive: [
            <span style={{ color: theme.textMuted, fontWeight: 600 }}>{p.part_number}</span>,
            p.name,
            <Badge label={cls.label} color={cls.color()} />,
            <span style={{ color: theme.textDim }}>{STATUS_LABEL[p.status] || p.status}</span>,
            qty(p.quantity),
            <span style={{ color: theme.textDim }}>{fmtDate(p.updated_at)}</span>,
          ],
        }[tab];

        return (
          <div key={p.id} onClick={() => onOpen(p)} style={{
            display: 'grid', gridTemplateColumns: grid, gap: 8, padding: '11px 16px',
            borderBottom: `1px solid ${theme.border}`, cursor: 'pointer', alignItems: 'center',
            fontSize: 12.5, color: theme.text,
            borderLeft: needsAttention(p) ? `3px solid ${theme.red}` : '3px solid transparent',
          }}
            onMouseEnter={e => e.currentTarget.style.background = theme.surfaceHover}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            {cells.map((c, i) => <div key={i} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c}</div>)}
          </div>
        );
      })}
    </>
  );
}

/* ── shared field components ───────────────────────────────────────────────── */
function Field({ label: lbl, styles, children, full }) {
  return (
    <div style={{ gridColumn: full ? '1 / -1' : 'auto' }}>
      <label style={styles.label}>{lbl}</label>
      {children}
    </div>
  );
}

/* ── add part modal ────────────────────────────────────────────────────────── */
function AddPartModal({ open, onClose, programs, users, locations, categories, styles, onCreated }) {
  const [f, setF] = useState({
    part_number: '', name: '', classification: 'production',
    quantity: '', lot_number: '', unit_of_measure: 'pcs', unit_size: '',
    location: '', category: '', description: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.part_number.trim() || !f.name.trim()) { setErr('Part number and name are required.'); return; }
    const qty = f.quantity ? parseFloat(f.quantity) : 0;
    if (qty > 0 && !f.lot_number.trim()) { setErr('A lot number is required when adding stock (quantity > 0).'); return; }
    setErr(''); setSaving(true);
    const payload = {
      part_number: f.part_number.trim(), name: f.name.trim(),
      classification: 'production', status: 'in_inventory',
      quantity: qty, lot_number: f.lot_number.trim(),
      unit_of_measure: f.unit_of_measure, unit_size: f.unit_size, description: f.description,
      location: f.location || null, category: f.category || null,
    };
    try { await jsonReq('POST', '/api/inventory/parts/', payload); onCreated(); }
    catch (e) { setErr(String(e.message || e)); }
    setSaving(false);
  };

  return (
    <Modal open={open} onClose={onClose} title="Add Production Part to Inventory" width={620}>
      {err && <div style={{ background: theme.red + '18', color: theme.red, padding: '8px 12px', borderRadius: 8, fontSize: 12, marginBottom: 12 }}>{err}</div>}

      <div style={{ color: theme.textDim, fontSize: 12, marginBottom: 14, lineHeight: 1.5 }}>
        For production parts going into sample inventory. Enter a quantity and lot number to add stock now, or leave quantity at 0 to just register the part. (Trial samples use the <strong>New Trial Sample</strong> button.)
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Part Number *" styles={styles}><input style={styles.input} value={f.part_number} onChange={e => set('part_number', e.target.value)} placeholder="e.g. TAR-200-A" /></Field>
        <Field label="Name *" styles={styles}><input style={styles.input} value={f.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Thick Aftermarket Pad" /></Field>
        <Field label="Class" styles={styles}><input style={{ ...styles.input, opacity: 0.6, cursor: 'not-allowed' }} value="Production" disabled readOnly /></Field>
        <Field label="Quantity" styles={styles}><input type="number" style={styles.input} value={f.quantity} onChange={e => set('quantity', e.target.value)} placeholder="0" /></Field>
        <Field label={`Lot Number${(f.quantity && parseFloat(f.quantity) > 0) ? ' *' : ''}`} styles={styles}><input style={styles.input} value={f.lot_number} onChange={e => set('lot_number', e.target.value)} placeholder="e.g. LOT-2026-014" /></Field>
        <Field label="Unit Type" styles={styles}><select style={styles.input} value={f.unit_of_measure} onChange={e => set('unit_of_measure', e.target.value)}>{UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}</select></Field>
        <Field label="Unit Size" styles={styles}><input style={styles.input} value={f.unit_size} onChange={e => set('unit_size', e.target.value)} placeholder='e.g. 12" x 12"' /></Field>
        <Field label="Location" styles={styles}><select style={styles.input} value={f.location} onChange={e => set('location', e.target.value)}><option value="">—</option>{locations.map(l => <option key={l.id} value={l.id}>{`[${l.code}] ${l.name}`}</option>)}</select></Field>
        <Field label="Category" styles={styles}><select style={styles.input} value={f.category} onChange={e => set('category', e.target.value)}><option value="">—</option>{categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>

        <Field label="Description" styles={styles} full><textarea style={{ ...styles.input, height: 54, resize: 'vertical' }} value={f.description} onChange={e => set('description', e.target.value)} /></Field>
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
        <button style={styles.btnGhost} onClick={onClose}>Cancel</button>
        <button style={styles.btnPrimary} disabled={saving} onClick={submit}>{saving ? 'Saving…' : 'Add Part'}</button>
      </div>
    </Modal>
  );
}



/* ── New Shipment modal (multi-line; lot picker + free-text toggle) ─────────── */
function AddShipmentModal({ open, onClose, customers, users, parts, styles, onCreated }) {
  const [f, setF] = useState({
    customer: '', contact_name: '', sales_contact: '', date_sent: todayISO(),
    business_type: '', order_reference: '', follow_up_date: '', notes_results: '',
  });
  const [lines, setLines] = useState([{ part: '', lot: '', free_text: false, lot_number_text: '', part_number_text: '', quantity: '' }]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  // Reset + default follow-up (date_sent + 3 months) when opened.
  useEffect(() => {
    if (!open) return;
    const d = new Date();
    const fu = new Date(d.getFullYear(), d.getMonth() + 3, d.getDate());
    setF({
      customer: '', contact_name: '', sales_contact: '', date_sent: todayISO(),
      business_type: '', order_reference: '', follow_up_date: fu.toISOString().slice(0, 10),
      notes_results: '',
    });
    setLines([{ part: '', lot: '', free_text: false, lot_number_text: '', part_number_text: '', quantity: '' }]);
    setErr('');
  }, [open]);

  // Recompute follow-up when date_sent changes (date_sent + 3 months).
  useEffect(() => {
    if (!f.date_sent) return;
    const d = new Date(f.date_sent);
    if (isNaN(d)) return;
    const fu = new Date(d.getFullYear(), d.getMonth() + 3, d.getDate());
    setF(p => ({ ...p, follow_up_date: fu.toISOString().slice(0, 10) }));
  }, [f.date_sent]);

  // Parts that have at least one lot with stock (for the stock-linked picker).
  const stockParts = (parts || []).filter(p => Number(p.total_quantity || 0) > 0);

  const setLine = (i, k, v) => setLines(ls => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const addLine = () => setLines(ls => [...ls, { part: '', lot: '', free_text: false, lot_number_text: '', part_number_text: '', quantity: '' }]);
  const removeLine = (i) => setLines(ls => ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls);

  const lotsForPart = (partId) => {
    const p = (parts || []).find(x => String(x.id) === String(partId));
    return (p?.lots || []).filter(l => Number(l.quantity) > 0);
  };

  const submit = async () => {
    if (!f.customer) { setErr('Customer is required.'); return; }
    if (!f.sales_contact) { setErr('Sales contact is required.'); return; }
    const payloadLines = [];
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      const qty = parseFloat(l.quantity);
      if (!qty || qty <= 0) { setErr(`Line ${i + 1}: enter a quantity.`); return; }
      if (l.free_text) {
        if (!l.lot_number_text.trim()) { setErr(`Line ${i + 1}: enter a lot number (free-text).`); return; }
        payloadLines.push({ free_text: true, part: null, lot: null, lot_number_text: l.lot_number_text.trim(), part_number_text: l.part_number_text.trim(), quantity: qty });
      } else {
        if (!l.lot) { setErr(`Line ${i + 1}: pick a lot, or switch to free-text.`); return; }
        payloadLines.push({ free_text: false, part: l.part || null, lot: l.lot, quantity: qty });
      }
    }
    setErr(''); setSaving(true);
    try {
      const res = await fetch('/api/inventory/shipments/', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf() },
        body: JSON.stringify({
          customer: f.customer, contact_name: f.contact_name.trim(),
          sales_contact: f.sales_contact, date_sent: f.date_sent,
          business_type: f.business_type.trim(), order_reference: f.order_reference.trim(),
          follow_up_date: f.follow_up_date || null, notes_results: f.notes_results.trim(),
          lines: payloadLines,
        }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || 'Create failed.'); }
      onCreated();
    } catch (e) { setErr(String(e.message || e)); }
    setSaving(false);
  };

  const sortedUsers = [...(users || [])].sort((a, b) => (a.full_name || a.username || '').localeCompare(b.full_name || b.username || ''));
  const sortedCustomers = [...(customers || [])].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  return (
    <Modal open={open} onClose={onClose} title="New Shipment" width={760}>
      {err && <div style={{ background: theme.red + '18', color: theme.red, padding: '8px 12px', borderRadius: 8, fontSize: 12, marginBottom: 12 }}>{err}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Customer *" styles={styles}><select style={styles.input} value={f.customer} onChange={e => set('customer', e.target.value)}><option value="">Select customer…</option>{sortedCustomers.map(c => <option key={c.id} value={c.id}>{c.name}{c.code ? ` (${c.code})` : ''}</option>)}</select></Field>
        <Field label="Contact" styles={styles}><input style={styles.input} value={f.contact_name} onChange={e => set('contact_name', e.target.value)} placeholder="Contact name" /></Field>
        <Field label="Sales Contact *" styles={styles}><select style={styles.input} value={f.sales_contact} onChange={e => set('sales_contact', e.target.value)}><option value="">Select user…</option>{sortedUsers.map(u => <option key={u.id} value={u.id}>{u.full_name || u.username}</option>)}</select></Field>
        <Field label="Date Sent" styles={styles}><input type="date" style={styles.input} value={f.date_sent} onChange={e => set('date_sent', e.target.value)} /></Field>
        <Field label="Business Type" styles={styles}><input style={styles.input} value={f.business_type} onChange={e => set('business_type', e.target.value)} placeholder="e.g. Automotive" /></Field>
        <Field label="Order / PO Ref" styles={styles}><input style={styles.input} value={f.order_reference} onChange={e => set('order_reference', e.target.value)} /></Field>
        <Field label="Follow-Up Date" styles={styles}><input type="date" style={styles.input} value={f.follow_up_date} onChange={e => set('follow_up_date', e.target.value)} /></Field>
      </div>

      {/* Lines */}
      <div style={{ marginTop: 16, marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ color: theme.accent, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>Parts Sent</div>
        <button style={{ ...styles.btnGhost, padding: '4px 10px', fontSize: 12 }} onClick={addLine}>+ Add line</button>
      </div>

      {lines.map((l, i) => {
        const lots = l.free_text ? [] : lotsForPart(l.part);
        return (
          <div key={i} style={{ padding: 10, marginBottom: 8, background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: theme.textMuted, cursor: 'pointer' }}>
                <input type="checkbox" checked={l.free_text} onChange={e => setLine(i, 'free_text', e.target.checked)} />
                Free-text lot (no stock link)
              </label>
              {lines.length > 1 && <button style={{ background: 'none', border: 'none', color: theme.red, cursor: 'pointer', fontSize: 16 }} onClick={() => removeLine(i)}>×</button>}
            </div>
            {l.free_text ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: 8 }}>
                <Field label="Part # (text)" styles={styles}><input style={styles.input} value={l.part_number_text} onChange={e => setLine(i, 'part_number_text', e.target.value)} placeholder="e.g. LEGACY-PART" /></Field>
                <Field label="Lot # (text) *" styles={styles}><input style={styles.input} value={l.lot_number_text} onChange={e => setLine(i, 'lot_number_text', e.target.value)} placeholder="e.g. HIST-LOT-42" /></Field>
                <Field label="Qty *" styles={styles}><input type="number" step="any" style={styles.input} value={l.quantity} onChange={e => setLine(i, 'quantity', e.target.value)} /></Field>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: 8 }}>
                <Field label="Part *" styles={styles}><select style={styles.input} value={l.part} onChange={e => { setLine(i, 'part', e.target.value); setLine(i, 'lot', ''); }}><option value="">Select part…</option>{stockParts.map(p => <option key={p.id} value={p.id}>{p.part_number} — {p.name}</option>)}</select></Field>
                <Field label="Lot *" styles={styles}><select style={styles.input} value={l.lot} onChange={e => setLine(i, 'lot', e.target.value)} disabled={!l.part}><option value="">{l.part ? 'Select lot…' : 'Pick a part first'}</option>{lots.map(lot => <option key={lot.id} value={lot.id}>{lot.lot_number} (×{lot.quantity})</option>)}</select></Field>
                <Field label="Qty *" styles={styles}><input type="number" step="any" style={styles.input} value={l.quantity} onChange={e => setLine(i, 'quantity', e.target.value)} /></Field>
              </div>
            )}
          </div>
        );
      })}

      <Field label="Notes / Results" styles={styles} full><textarea style={{ ...styles.input, height: 54, resize: 'vertical' }} value={f.notes_results} onChange={e => set('notes_results', e.target.value)} /></Field>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
        <button style={styles.btnGhost} onClick={onClose}>Cancel</button>
        <button style={styles.btnPrimary} disabled={saving} onClick={submit}>{saving ? 'Creating…' : 'Create Shipment'}</button>
      </div>
    </Modal>
  );
}

/* ── New Trial Sample modal (TYYXXX auto/manual numbering + standard form) ──── */
// The 16-field development spec form. Fields are numeric (units are fixed and
// shown in labels). Creating a trial auto-assigns the next T-number unless the
// user requests a specific unused one.
const TRIAL_SPEC_FIELDS = [
  ['target_basis_weight', 'Target Basis Wt.', 'g/sqm'],
  ['actual_basis_weight', 'Actual Basis Wt.', 'g/sqm'],
  ['target_thickness', 'Target Thickness', 'µm'],
  ['actual_thickness', 'Actual Thickness', 'µm'],
  ['compression_set_um', 'Compression Set', 'µm'],
  ['compression_set_pct', 'Compression Set', '%'],
  ['compressive_modulus', 'Compressive Modulus', 'Pa'],
  ['taber_stiffness', 'Taber Stiffness', 'Taber'],
  ['roughness_ra', 'Roughness, Ra', 'µm'],
  ['d_do', 'D/Do', ''],
  ['gurley', 'Gurley', 'G-s'],
  ['skeletal_density', 'Skeletal Density', 'g/cm³'],
];

function AddTrialModal({ open, onClose, styles, onCreated }) {
  const [f, setF] = useState({
    name: '', description: '', target_customer: '', date_completed: '',
    comments: '',
  });
  const [specs, setSpecs] = useState({});
  const [numberMode, setNumberMode] = useState('auto');   // 'auto' | 'manual'
  const [autoNumber, setAutoNumber] = useState('');
  const [manualNumber, setManualNumber] = useState('');
  const [manualCheck, setManualCheck] = useState(null);    // {valid, detail}
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const setSpec = (k, v) => setSpecs(p => ({ ...p, [k]: v }));

  // Fetch the suggested next number when the modal opens.
  useEffect(() => {
    if (!open) return;
    setF({ name: '', description: '', target_customer: '', date_completed: '', comments: '' });
    setSpecs({}); setNumberMode('auto'); setManualNumber(''); setManualCheck(null); setErr('');
    fetch('/api/inventory/parts/next_trial_number/', { credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setAutoNumber(d.next_number))
      .catch(() => setAutoNumber(''));
  }, [open]);

  // Validate a manual number as the user types (debounced-ish on blur).
  const checkManual = async () => {
    const v = manualNumber.trim().toUpperCase();
    if (!v) { setManualCheck(null); return; }
    try {
      const res = await fetch('/api/inventory/parts/check_trial_number/', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf() },
        body: JSON.stringify({ part_number: v }),
      });
      const d = await res.json();
      setManualCheck(d);
    } catch (e) { setManualCheck({ valid: false, detail: 'Could not check number.' }); }
  };

  const submit = async () => {
    if (!f.name.trim()) { setErr('Part name is required.'); return; }
    if (numberMode === 'manual') {
      if (!manualNumber.trim()) { setErr('Enter a trial number, or switch to auto.'); return; }
      if (manualCheck && !manualCheck.valid) { setErr(manualCheck.detail || 'That number is not available.'); return; }
    }
    setErr(''); setSaving(true);
    const payload = {
      name: f.name.trim(), description: f.description,
      target_customer: f.target_customer, notes: f.comments,
      date_completed: f.date_completed || null,
    };
    Object.entries(specs).forEach(([k, v]) => { if (v !== '' && v != null) payload[k] = v; });
    if (numberMode === 'manual') payload.part_number = manualNumber.trim().toUpperCase();
    try {
      const res = await fetch('/api/inventory/parts/create_trial/', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Create failed.');
      }
      onCreated();
    } catch (e) { setErr(String(e.message || e)); }
    setSaving(false);
  };

  return (
    <Modal open={open} onClose={onClose} title="New Trial Sample" width={680}>
      {err && <div style={{ background: theme.red + '18', color: theme.red, padding: '8px 12px', borderRadius: 8, fontSize: 12, marginBottom: 12 }}>{err}</div>}

      {/* T-number picker */}
      <div style={{ marginBottom: 14, padding: 12, background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 8 }}>
        <label style={styles.label}>Trial Number</label>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <button onClick={() => setNumberMode('auto')} style={{
            flex: 1, padding: '8px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            border: numberMode === 'auto' ? `1px solid ${theme.accent}` : `1px solid ${theme.border}`,
            background: numberMode === 'auto' ? theme.accent + '22' : 'transparent',
            color: numberMode === 'auto' ? theme.accent : theme.textMuted,
          }}>Auto ({autoNumber || '…'})</button>
          <button onClick={() => setNumberMode('manual')} style={{
            flex: 1, padding: '8px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            border: numberMode === 'manual' ? `1px solid ${theme.accent}` : `1px solid ${theme.border}`,
            background: numberMode === 'manual' ? theme.accent + '22' : 'transparent',
            color: numberMode === 'manual' ? theme.accent : theme.textMuted,
          }}>Choose a number</button>
        </div>
        {numberMode === 'auto' ? (
          <div style={{ color: theme.textDim, fontSize: 12 }}>
            Will be assigned <strong style={{ color: theme.text }}>{autoNumber || '…'}</strong> (next available this year).
          </div>
        ) : (
          <div>
            <input style={styles.input} value={manualNumber} placeholder="e.g. T26018"
              onChange={e => { setManualNumber(e.target.value); setManualCheck(null); }}
              onBlur={checkManual} />
            {manualCheck && (
              <div style={{ fontSize: 12, marginTop: 4, color: manualCheck.valid ? theme.green : theme.red }}>
                {manualCheck.valid ? '✓ Available' : (manualCheck.detail || 'Not available')}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Part Name *" styles={styles}><input style={styles.input} value={f.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Thin GDL trial" /></Field>
        <Field label="Target Customer" styles={styles}><input style={styles.input} value={f.target_customer} onChange={e => set('target_customer', e.target.value)} placeholder="Customer name" /></Field>
        <Field label="Description" styles={styles} full><textarea style={{ ...styles.input, height: 48, resize: 'vertical' }} value={f.description} onChange={e => set('description', e.target.value)} /></Field>

        {TRIAL_SPEC_FIELDS.map(([key, label, unit]) => (
          <Field key={key} label={unit ? `${label} (${unit})` : label} styles={styles}>
            <input type="number" step="any" style={styles.input} value={specs[key] ?? ''} onChange={e => setSpec(key, e.target.value)} />
          </Field>
        ))}

        <Field label="Date Completed" styles={styles}><input type="date" style={styles.input} value={f.date_completed} onChange={e => set('date_completed', e.target.value)} /></Field>
        <Field label="Comments" styles={styles} full><textarea style={{ ...styles.input, height: 54, resize: 'vertical' }} value={f.comments} onChange={e => set('comments', e.target.value)} /></Field>
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
        <button style={styles.btnGhost} onClick={onClose}>Cancel</button>
        <button style={styles.btnPrimary} disabled={saving} onClick={submit}>{saving ? 'Creating…' : 'Create Trial'}</button>
      </div>
    </Modal>
  );
}


/* ── Lots management (stock lives on lots; add/edit/remove per part) ────────── */
function LotsSection({ part, locations, styles, onChanged }) {
  const [lots, setLots] = useState([]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ lot_number: '', quantity: '', location: '', date_made: '', notes: '' });
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = () => fetch(`/api/inventory/lots/?part=${part.id}`, { credentials: 'same-origin' })
    .then(r => r.ok ? r.json() : []).then(d => setLots(Array.isArray(d) ? d : (d.results || []))).catch(() => setLots([]));
  useEffect(() => { load(); }, [part.id]);

  const resetDraft = () => setDraft({ lot_number: '', quantity: '', location: '', date_made: '', notes: '' });

  const saveNew = async () => {
    if (!draft.lot_number.trim()) { setErr('Lot number is required.'); return; }
    setBusy(true); setErr('');
    try {
      const res = await fetch('/api/inventory/lots/', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf() },
        body: JSON.stringify({
          part: part.id, lot_number: draft.lot_number.trim(),
          quantity: draft.quantity === '' ? 0 : parseFloat(draft.quantity),
          location: draft.location || null, date_made: draft.date_made || null, notes: draft.notes,
        }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || JSON.stringify(d)); }
      resetDraft(); setAdding(false); await load(); onChanged && onChanged();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  };

  const saveEdit = async (lot) => {
    setBusy(true); setErr('');
    try {
      const res = await fetch(`/api/inventory/lots/${lot.id}/`, {
        method: 'PATCH', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf() },
        body: JSON.stringify({
          lot_number: lot.lot_number, quantity: lot.quantity === '' ? 0 : parseFloat(lot.quantity),
          location: lot.location || null, date_made: lot.date_made || null, notes: lot.notes || '',
        }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || JSON.stringify(d)); }
      setEditId(null); await load(); onChanged && onChanged();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  };

  const removeLot = async (id) => {
    setBusy(true); setErr('');
    try {
      await fetch(`/api/inventory/lots/${id}/`, { method: 'DELETE', credentials: 'same-origin', headers: { 'X-CSRFToken': csrf() } });
      await load(); onChanged && onChanged();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  };

  const total = lots.reduce((s, l) => s + Number(l.quantity || 0), 0);
  const inp = { ...styles.input, padding: '5px 8px', fontSize: 12 };

  return (
    <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${theme.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ color: theme.accent, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          Lots · On hand: {total}
        </div>
        {!adding && <button style={{ ...styles.btnGhost, padding: '4px 10px', fontSize: 12 }} onClick={() => { resetDraft(); setAdding(true); }}>+ Add Lot</button>}
      </div>
      {err && <div style={{ background: theme.red + '18', color: theme.red, padding: '6px 10px', borderRadius: 6, fontSize: 12, marginBottom: 8 }}>{err}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 70px 1fr 30px', gap: 8, padding: '0 0 6px', fontSize: 10, fontWeight: 700, color: theme.textDim, textTransform: 'uppercase' }}>
        <div>Lot #</div><div>Qty</div><div>Location</div><div></div>
      </div>

      {lots.map(l => editId === l.id ? (
        <div key={l.id} style={{ display: 'grid', gridTemplateColumns: '1fr 70px 1fr 30px', gap: 8, marginBottom: 6, alignItems: 'center' }}>
          <input style={inp} value={l.lot_number} onChange={e => setLots(ls => ls.map(x => x.id === l.id ? { ...x, lot_number: e.target.value } : x))} />
          <input type="number" step="any" style={inp} value={l.quantity} onChange={e => setLots(ls => ls.map(x => x.id === l.id ? { ...x, quantity: e.target.value } : x))} />
          <select style={inp} value={l.location || ''} onChange={e => setLots(ls => ls.map(x => x.id === l.id ? { ...x, location: e.target.value } : x))}>
            <option value="">—</option>{locations.map(loc => <option key={loc.id} value={loc.id}>{`[${loc.code}] ${loc.name}`}</option>)}
          </select>
          <div style={{ display: 'flex', gap: 2 }}>
            <button style={{ ...styles.btnPrimary, padding: '3px 6px', fontSize: 11 }} disabled={busy} onClick={() => saveEdit(l)}>✓</button>
          </div>
        </div>
      ) : (
        <div key={l.id} style={{ display: 'grid', gridTemplateColumns: '1fr 70px 1fr 30px', gap: 8, marginBottom: 6, alignItems: 'center', fontSize: 12.5, color: theme.text }}>
          <div style={{ fontWeight: 600 }}>{l.lot_number}</div>
          <div style={{ color: Number(l.quantity) > 0 ? theme.text : theme.textDim }}>{l.quantity}</div>
          <div style={{ color: theme.textMuted }}>{l.location_name || '—'}</div>
          <div style={{ display: 'flex', gap: 4 }}>
            <button title="Edit" style={{ background: 'none', border: 'none', color: theme.textDim, cursor: 'pointer', fontSize: 12 }} onClick={() => setEditId(l.id)}>✎</button>
            <button title="Remove" style={{ background: 'none', border: 'none', color: theme.red, cursor: 'pointer', fontSize: 14 }} onClick={() => removeLot(l.id)}>×</button>
          </div>
        </div>
      ))}

      {adding && (
        <div style={{ marginTop: 8, padding: 10, background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Field label="Lot Number *" styles={styles}><input style={inp} value={draft.lot_number} onChange={e => setDraft(d => ({ ...d, lot_number: e.target.value }))} placeholder="e.g. LOT-A" /></Field>
            <Field label="Quantity" styles={styles}><input type="number" step="any" style={inp} value={draft.quantity} onChange={e => setDraft(d => ({ ...d, quantity: e.target.value }))} placeholder="0" /></Field>
            <Field label="Location" styles={styles}><select style={inp} value={draft.location} onChange={e => setDraft(d => ({ ...d, location: e.target.value }))}><option value="">—</option>{locations.map(loc => <option key={loc.id} value={loc.id}>{`[${loc.code}] ${loc.name}`}</option>)}</select></Field>
            <Field label="Date Made" styles={styles}><input type="date" style={inp} value={draft.date_made} onChange={e => setDraft(d => ({ ...d, date_made: e.target.value }))} /></Field>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button style={{ ...styles.btnGhost, padding: '4px 10px', fontSize: 12 }} onClick={() => { setAdding(false); setErr(''); }}>Cancel</button>
            <button style={{ ...styles.btnPrimary, padding: '4px 10px', fontSize: 12 }} disabled={busy} onClick={saveNew}>Add Lot</button>
          </div>
        </div>
      )}
      {lots.length === 0 && !adding && <div style={{ fontSize: 12, color: theme.textDim }}>No lots yet. Add one to put this part in stock.</div>}
    </div>
  );
}

/* ── detail / edit slide-over ──────────────────────────────────────────────── */
function DetailPanel({ part, onClose, programs, users, locations, categories, styles, onChanged, onMovedAway }) {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [moveOpen, setMoveOpen] = useState(false);
  const [tx, setTx] = useState([]);

  useEffect(() => {
    setF({
      name: part.name || '', description: part.description || '', classification: part.classification || 'development',
      quantity: part.quantity ?? '', unit_of_measure: part.unit_of_measure || 'pcs', unit_size: part.unit_size || '',
      location: part.location || '', category: part.category || '',
      material_specification: part.material_specification || '', test_results_summary: part.test_results_summary || '',
      test_company: part.test_company || '', sent_date: part.sent_date || '', expected_return_date: part.expected_return_date || '',
      test_outcome: part.test_outcome || 'pending',
      assigned_engineer: part.assigned_engineer || '', program: part.program || '', notes: part.notes || '',
      target_customer: part.target_customer || '',
      target_basis_weight: part.target_basis_weight ?? '', actual_basis_weight: part.actual_basis_weight ?? '',
      target_thickness: part.target_thickness ?? '', actual_thickness: part.actual_thickness ?? '',
      compression_set_um: part.compression_set_um ?? '', compression_set_pct: part.compression_set_pct ?? '',
      compressive_modulus: part.compressive_modulus ?? '', taber_stiffness: part.taber_stiffness ?? '',
      roughness_ra: part.roughness_ra ?? '', d_do: part.d_do ?? '', gurley: part.gurley ?? '',
      skeletal_density: part.skeletal_density ?? '', date_completed: part.date_completed || '',
    });
    fetch(`/api/inventory/transactions/?part=${part.id}`, { credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : []).then(d => setTx(Array.isArray(d) ? d : (d.results || []))).catch(() => setTx([]));
  }, [part.id]);

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true); setMsg('');
    const payload = {
      name: f.name, description: f.description, classification: f.classification,
      unit_of_measure: f.unit_of_measure, unit_size: f.unit_size,
      category: f.category || null,
      material_specification: f.material_specification, test_results_summary: f.test_results_summary,
    };
    if (part.status === 'testing') {
      payload.test_company = f.test_company; payload.sent_date = f.sent_date || null;
      payload.expected_return_date = f.expected_return_date || null; payload.test_outcome = f.test_outcome;
    }
    if (part.status === 'in_development') {
      payload.assigned_engineer = f.assigned_engineer || null; payload.program = f.program || null; payload.notes = f.notes;
    }
    if (part.classification === 'trial') {
      payload.target_customer = f.target_customer;
      const num = (v) => (v === '' || v == null ? null : parseFloat(v));
      payload.target_basis_weight = num(f.target_basis_weight);
      payload.actual_basis_weight = num(f.actual_basis_weight);
      payload.target_thickness = num(f.target_thickness);
      payload.actual_thickness = num(f.actual_thickness);
      payload.compression_set_um = num(f.compression_set_um);
      payload.compression_set_pct = num(f.compression_set_pct);
      payload.compressive_modulus = num(f.compressive_modulus);
      payload.taber_stiffness = num(f.taber_stiffness);
      payload.roughness_ra = num(f.roughness_ra);
      payload.d_do = num(f.d_do);
      payload.gurley = num(f.gurley);
      payload.skeletal_density = num(f.skeletal_density);
      payload.date_completed = f.date_completed || null;
    }
    try { await jsonReq('PATCH', `/api/inventory/parts/${part.id}/`, payload); setMsg('Saved'); await onChanged(part.id); setTimeout(() => setMsg(''), 1500); }
    catch (e) { setMsg('Error: ' + String(e.message || e)); }
    setSaving(false);
  };

  const cls = CLASS_CFG[part.classification] || { label: part.classification, color: () => theme.textDim };
  const sectionColor = (SECTIONS[part.status] ? SECTIONS[part.status].color() : theme.textDim);
  const overdue = isTestingOverdue(part);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 150, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 560, maxWidth: '100%', height: '100%', background: theme.surface, borderLeft: `1px solid ${theme.border}`, overflow: 'auto', padding: 22 }}>
        {/* header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
          <div>
            <div style={{ color: theme.accent, fontSize: 13, fontWeight: 700 }}>{part.part_number}</div>
            <div style={{ color: theme.text, fontSize: 18, fontWeight: 700, marginTop: 2 }}>{part.name}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: theme.textDim, fontSize: 24, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 18, flexWrap: 'wrap' }}>
          <Badge label={STATUS_LABEL[part.status] || part.status} color={sectionColor} />
          <Badge label={cls.label} color={cls.color()} />
          {part.status === 'in_development' && <span style={{ color: isDevStale(part) ? theme.red : theme.textDim, fontSize: 11 }}>{part.days_in_section || 0} days in development{isDevStale(part) ? ' — stale' : ''}</span>}
          {overdue && <span style={{ color: theme.red, fontSize: 11, fontWeight: 600 }}>⚠ Return overdue</span>}
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
          <button style={styles.btnGhost} onClick={() => setMoveOpen(true)}>Move to another section →</button>
          <button style={styles.btnGhost} onClick={async () => {
            const verb = part.archived ? 'unarchive' : 'archive';
            try {
              await jsonReq('POST', `/api/inventory/parts/${part.id}/${verb}/`, {});
              await onMovedAway();
            } catch (e) { /* noop */ }
          }}>{part.archived ? 'Unarchive' : 'Archive'}</button>
        </div>

        {/* editable details */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Name" styles={styles} full><input style={styles.input} value={f.name} onChange={e => set('name', e.target.value)} /></Field>
          <Field label="Class" styles={styles}><select style={styles.input} value={f.classification} onChange={e => set('classification', e.target.value)}>{Object.entries(CLASS_CFG).map(([id, c]) => <option key={id} value={id}>{c.label}</option>)}</select></Field>
          <Field label="Unit Type" styles={styles}><select style={styles.input} value={f.unit_of_measure} onChange={e => set('unit_of_measure', e.target.value)}>{UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}</select></Field>
          <Field label="Unit Size" styles={styles}><input style={styles.input} value={f.unit_size} onChange={e => set('unit_size', e.target.value)} /></Field>
        </div>

        {/* testing group */}
        {part.status === 'testing' && (
          <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${theme.border}` }}>
            <div style={{ color: theme.yellow, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 10 }}>Testing</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Company" styles={styles} full><input style={styles.input} value={f.test_company} onChange={e => set('test_company', e.target.value)} /></Field>
              <Field label="Sent Date" styles={styles}><input type="date" style={styles.input} value={f.sent_date || ''} onChange={e => set('sent_date', e.target.value)} /></Field>
              <Field label="Expected Return" styles={styles}><input type="date" style={{ ...styles.input, borderColor: overdue ? theme.red : theme.border }} value={f.expected_return_date || ''} onChange={e => set('expected_return_date', e.target.value)} /></Field>
              <Field label="Outcome" styles={styles} full><select style={styles.input} value={f.test_outcome} onChange={e => set('test_outcome', e.target.value)}>{Object.entries(OUTCOME_CFG).map(([id, o]) => <option key={id} value={id}>{o.label}</option>)}</select></Field>
            </div>
          </div>
        )}

        {/* development group */}
        {part.status === 'in_development' && (
          <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${theme.border}` }}>
            <div style={{ color: theme.accent, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 10 }}>Development</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Assigned Engineer" styles={styles}><select style={styles.input} value={f.assigned_engineer} onChange={e => set('assigned_engineer', e.target.value)}><option value="">—</option>{users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.username}</option>)}</select></Field>
              <Field label="Program" styles={styles}><select style={styles.input} value={f.program} onChange={e => set('program', e.target.value)}><option value="">—</option>{programs.map(pr => <option key={pr.id} value={pr.id}>{`${pr.code} — ${pr.name}`}</option>)}</select></Field>
              <Field label="Notes" styles={styles} full><textarea style={{ ...styles.input, height: 70, resize: 'vertical' }} value={f.notes} onChange={e => set('notes', e.target.value)} /></Field>
            </div>
          </div>
        )}

        {part.classification === 'trial' && (
          <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${theme.border}` }}>
            <div style={{ color: theme.accent, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 10 }}>Trial Specs</div>
            <div style={{ marginBottom: 10 }}>
              <Field label="Target Customer" styles={styles} full><input style={styles.input} value={f.target_customer} onChange={e => set('target_customer', e.target.value)} /></Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[
                ['target_basis_weight', 'Target Basis Wt.', 'g/sqm'],
                ['actual_basis_weight', 'Actual Basis Wt.', 'g/sqm'],
                ['target_thickness', 'Target Thickness', 'µm'],
                ['actual_thickness', 'Actual Thickness', 'µm'],
                ['compression_set_um', 'Compression Set', 'µm'],
                ['compression_set_pct', 'Compression Set', '%'],
                ['compressive_modulus', 'Compressive Modulus', 'Pa'],
                ['taber_stiffness', 'Taber Stiffness', 'Taber'],
                ['roughness_ra', 'Roughness, Ra', 'µm'],
                ['d_do', 'D/Do', ''],
                ['gurley', 'Gurley', 'G-s'],
                ['skeletal_density', 'Skeletal Density', 'g/cm³'],
              ].map(([key, label, unit]) => (
                <Field key={key} label={unit ? `${label} (${unit})` : label} styles={styles}>
                  <input type="number" step="any" style={styles.input} value={f[key] ?? ''} onChange={e => set(key, e.target.value)} />
                </Field>
              ))}
              <Field label="Date Completed" styles={styles}><input type="date" style={styles.input} value={f.date_completed} onChange={e => set('date_completed', e.target.value)} /></Field>
            </div>
          </div>
        )}

        {/* engineering data */}
        <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${theme.border}` }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
            <Field label="Description" styles={styles}><textarea style={{ ...styles.input, height: 50, resize: 'vertical' }} value={f.description} onChange={e => set('description', e.target.value)} /></Field>
            <Field label="Material Specification" styles={styles}><textarea style={{ ...styles.input, height: 50, resize: 'vertical' }} value={f.material_specification} onChange={e => set('material_specification', e.target.value)} /></Field>
            <Field label="Test Results Summary" styles={styles}><textarea style={{ ...styles.input, height: 50, resize: 'vertical' }} value={f.test_results_summary} onChange={e => set('test_results_summary', e.target.value)} /></Field>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 16 }}>
          <button style={styles.btnPrimary} disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save Changes'}</button>
          {msg && <span style={{ color: msg.startsWith('Error') ? theme.red : theme.green, fontSize: 12 }}>{msg}</span>}
        </div>

        <LotsSection part={part} locations={locations} styles={styles} onChanged={() => onChanged(part.id)} />
        <Attachments part={part} styles={styles} onChanged={() => onChanged(part.id)} />
        <ActivityLog tx={tx} />
      </div>

      {moveOpen && (
        <MoveModal part={part} locations={locations} styles={styles}
          onClose={() => setMoveOpen(false)}
          onMoved={async (stillInTab) => { setMoveOpen(false); if (stillInTab) await onChanged(part.id); else await onMovedAway(); }} />
      )}
    </div>
  );
}

/* ── attachments ───────────────────────────────────────────────────────────── */
function Attachments({ part, styles, onChanged }) {
  const [list, setList] = useState(part.attachments || []);
  const [type, setType] = useState('test_report');
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { setList(part.attachments || []); }, [part.attachments, part.id]);

  const refresh = async () => {
    try { const r = await fetch(`/api/inventory/attachments/?part=${part.id}`, { credentials: 'same-origin' }); if (r.ok) { const d = await r.json(); setList(Array.isArray(d) ? d : (d.results || [])); } } catch (e) {}
  };

  const upload = async () => {
    if (!file) { setErr('Choose a file first.'); return; }
    setErr(''); setBusy(true);
    const fd = new FormData();
    fd.append('file', file); fd.append('attachment_type', type);
    if (title) fd.append('title', title);
    try {
      const res = await fetch(`/api/inventory/parts/${part.id}/upload_attachment/`, { method: 'POST', credentials: 'same-origin', headers: { 'X-CSRFToken': csrf() }, body: fd });
      if (!res.ok) throw new Error('Upload failed');
      setFile(null); setTitle('');
      await refresh(); onChanged();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  };

  const remove = async (id) => {
    try { await jsonReq('DELETE', `/api/inventory/attachments/${id}/`); await refresh(); onChanged(); } catch (e) { setErr(String(e.message || e)); }
  };

  return (
    <div style={{ marginTop: 22, paddingTop: 16, borderTop: `1px solid ${theme.border}` }}>
      <div style={{ color: theme.text, fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Attachments</div>
      {list.length === 0 && <div style={{ color: theme.textDim, fontSize: 12, marginBottom: 10 }}>No files attached.</div>}
      {list.map(a => (
        <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 8, marginBottom: 6 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <a href={a.file_url || a.file} target="_blank" rel="noreferrer" style={{ color: theme.accent, fontSize: 12.5, fontWeight: 600, textDecoration: 'none' }}>{a.title}</a>
            <div style={{ color: theme.textDim, fontSize: 10.5, marginTop: 2 }}>{ATTACHMENT_LABEL[a.attachment_type] || a.attachment_type}{a.uploaded_by_name ? ` · ${a.uploaded_by_name}` : ''}{a.uploaded_at ? ` · ${fmtDate(a.uploaded_at)}` : ''}</div>
          </div>
          <button onClick={() => remove(a.id)} style={{ background: 'none', border: 'none', color: theme.textDim, cursor: 'pointer', fontSize: 16 }} title="Delete">×</button>
        </div>
      ))}

      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <select style={{ ...styles.input, width: 'auto', flex: '0 0 auto' }} value={type} onChange={e => setType(e.target.value)}>{ATTACHMENT_TYPES.map(([id, lbl]) => <option key={id} value={id}>{lbl}</option>)}</select>
        <input style={{ ...styles.input, flex: 1, minWidth: 120 }} placeholder="Title (optional)" value={title} onChange={e => setTitle(e.target.value)} />
        <input type="file" onChange={e => setFile(e.target.files[0])} style={{ color: theme.textMuted, fontSize: 11, flex: '1 1 160px' }} />
        <button style={styles.btnPrimary} disabled={busy} onClick={upload}>{busy ? '…' : 'Upload'}</button>
      </div>
      {err && <div style={{ color: theme.red, fontSize: 11, marginTop: 6 }}>{err}</div>}
    </div>
  );
}

/* ── activity log ──────────────────────────────────────────────────────────── */
function ActivityLog({ tx }) {
  if (!tx || tx.length === 0) return null;
  return (
    <div style={{ marginTop: 22, paddingTop: 16, borderTop: `1px solid ${theme.border}` }}>
      <div style={{ color: theme.text, fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Activity</div>
      {tx.map(t => (
        <div key={t.id} style={{ display: 'flex', gap: 10, padding: '6px 0', fontSize: 12 }}>
          <div style={{ width: 6, height: 6, borderRadius: 3, background: theme.accent, marginTop: 6, flex: '0 0 auto' }} />
          <div style={{ flex: 1 }}>
            <div style={{ color: theme.text }}>{t.transaction_type_display}{t.notes ? <span style={{ color: theme.textMuted }}> — {t.notes}</span> : null}</div>
            <div style={{ color: theme.textDim, fontSize: 10.5, marginTop: 1 }}>{t.performed_by_name || 'system'} · {fmtDateTime(t.timestamp)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── move modal ────────────────────────────────────────────────────────────── */
function MoveModal({ part, locations, styles, onClose, onMoved }) {
  const destinations = [
    ['in_inventory', 'In Stock'], ['testing', 'Testing'],
    ['in_development', 'In Development'], ['scrapped', 'Scrapped'],
  ].filter(([id]) => id !== part.status);

  const [to, setTo] = useState(destinations[0][0]);
  const [company, setCompany] = useState(part.test_company || '');
  const [sent, setSent] = useState(todayISO());
  const [expected, setExpected] = useState(addDaysISO(todayISO(), 90));
  const [outcome, setOutcome] = useState('pass');
  const [location, setLocation] = useState(part.location || '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const onSent = (v) => { setSent(v); setExpected(addDaysISO(v, 90)); };

  const confirm = async () => {
    if (to === 'testing' && !company.trim()) { setErr('Company is required when sending for testing.'); return; }
    setErr(''); setBusy(true);
    const payload = { to_status: to, note };
    if (to === 'testing') { payload.test_company = company.trim(); payload.sent_date = sent; payload.expected_return_date = expected; }
    if (to === 'in_inventory' && part.status === 'testing') payload.test_outcome = outcome;
    if (location) payload.location = location;
    try {
      await jsonReq('POST', `/api/inventory/parts/${part.id}/move/`, payload);
      // still visible in the current tab only if destination matches the section we're viewing
      onMoved(false);
    } catch (e) { setErr(String(e.message || e)); setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Move ${part.part_number}`} width={460}>
      {err && <div style={{ background: theme.red + '18', color: theme.red, padding: '8px 12px', borderRadius: 8, fontSize: 12, marginBottom: 12 }}>{err}</div>}
      <div style={{ marginBottom: 14 }}>
        <label style={styles.label}>Move to</label>
        <select style={styles.input} value={to} onChange={e => setTo(e.target.value)}>
          {destinations.map(([id, lbl]) => <option key={id} value={id}>{lbl}</option>)}
        </select>
      </div>

      {to === 'testing' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <Field label="Company *" styles={styles} full><input style={styles.input} value={company} onChange={e => setCompany(e.target.value)} placeholder="Testing company" /></Field>
          <Field label="Sent Date" styles={styles}><input type="date" style={styles.input} value={sent} onChange={e => onSent(e.target.value)} /></Field>
          <Field label="Expected Return" styles={styles}><input type="date" style={styles.input} value={expected} onChange={e => setExpected(e.target.value)} /></Field>
          <div style={{ gridColumn: '1 / -1', color: theme.textDim, fontSize: 11 }}>Defaults to 90 days after the sent date.</div>
        </div>
      )}

      {to === 'in_inventory' && part.status === 'testing' && (
        <div style={{ marginBottom: 14 }}>
          <label style={styles.label}>Test Outcome</label>
          <select style={styles.input} value={outcome} onChange={e => setOutcome(e.target.value)}>
            {Object.entries(OUTCOME_CFG).map(([id, o]) => <option key={id} value={id}>{o.label}</option>)}
          </select>
        </div>
      )}

      {(to === 'in_inventory') && (
        <div style={{ marginBottom: 14 }}>
          <label style={styles.label}>Location</label>
          <select style={styles.input} value={location} onChange={e => setLocation(e.target.value)}>
            <option value="">—</option>
            {locations.map(l => <option key={l.id} value={l.id}>{`[${l.code}] ${l.name}`}</option>)}
          </select>
        </div>
      )}

      <div style={{ marginBottom: 14 }}>
        <label style={styles.label}>Note (optional)</label>
        <input style={styles.input} value={note} onChange={e => setNote(e.target.value)} placeholder="Reason / reference" />
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button style={styles.btnGhost} onClick={onClose}>Cancel</button>
        <button style={styles.btnPrimary} disabled={busy} onClick={confirm}>{busy ? 'Moving…' : 'Confirm Move'}</button>
      </div>
    </Modal>
  );
}
